export const CORE_PACKAGE_NAME = "@gridflow/core" as const;
export const CORE_RUNTIME_PHASE = "m2-corelogic-mvp" as const;

export interface CoreRuntimeInfo {
  readonly packageName: typeof CORE_PACKAGE_NAME;
  readonly phase: typeof CORE_RUNTIME_PHASE;
}

export type NodeRole = "origin" | "target";

export interface GridPoint {
  readonly x: number;
  readonly y: number;
}

export interface OriginNodeConfig {
  readonly color: string;
  readonly role: "origin";
  readonly position: GridPoint;
  readonly targetLength: number;
}

export interface TargetNodeConfig {
  readonly color: string;
  readonly role: "target";
  readonly position: GridPoint;
}

export type NodeConfig = OriginNodeConfig | TargetNodeConfig;

export interface LevelConfig {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly fillRequired: boolean;
  readonly obstacles?: readonly GridPoint[];
  readonly nodes: readonly NodeConfig[];
}

export interface Cell {
  readonly x: number;
  readonly y: number;
  readonly isObstacle: boolean;
  readonly hasNode: boolean;
  readonly nodeRole: NodeRole | null;
  readonly nodeColor: string | null;
  occupiedByColor: string | null;
  pathIndex: number | null;
}

export interface ColorNodePair {
  readonly origin: OriginNodeConfig;
  readonly target: TargetNodeConfig;
}

export interface PathState {
  readonly color: string;
  readonly targetLength: number;
  cells: GridPoint[];
  isLinked: boolean;
}

export interface LevelState {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly fillRequired: boolean;
  readonly fillableCount: number;
  readonly grid: Cell[][];
  readonly paths: Map<string, PathState>;
  readonly nodesByColor: Map<string, ColorNodePair>;
}

export interface CellSnapshot {
  readonly x: number;
  readonly y: number;
  readonly isObstacle: boolean;
  readonly hasNode: boolean;
  readonly nodeRole: NodeRole | null;
  readonly nodeColor: string | null;
  readonly occupiedByColor: string | null;
  readonly pathIndex: number | null;
}

export interface PathSnapshot {
  readonly color: string;
  readonly targetLength: number;
  readonly currentLength: number;
  readonly remainingLength: number;
  readonly isLinked: boolean;
  readonly isComplete: boolean;
  readonly cells: readonly GridPoint[];
}

export interface LevelSnapshot {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly fillRequired: boolean;
  readonly fillableCount: number;
  readonly occupiedCount: number;
  readonly hasWon: boolean;
  readonly grid: readonly (readonly CellSnapshot[])[];
  readonly paths: readonly PathSnapshot[];
}

export interface PathCommand {
  execute(): void;
  undo(): void;
}

export class GridFlowCoreError extends Error {
  public constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "GridFlowCoreError";
  }
}
